import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AppLink } from "../lib/route-primitives";
import { ArrowLeft, Clapperboard, Pause } from "lucide-react";
import type { RejiPlaylistResponse } from "@wedding/shared";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";
import { useWedding } from "../contexts/wedding-context";
import { useOrganizerApi } from "../hooks/use-organizer-api";
import { useRejiWs } from "../hooks/use-reji-ws";
import { RejiPreview } from "../components/reji/reji-preview";
import { RejiTransport } from "../components/reji/reji-transport";
import { RejiGrid } from "../components/reji/reji-grid";
import { PresenceFacepile, AudienceList } from "@wedding/slideshow-ui";
import { RejiPresenceHeader } from "../components/reji/reji-presence-sheet";

type ControlAction =
  | "pause" | "resume" | "next" | "prev"
  | "goto" | "hide" | "unhide" | "spotlight" | "move";

/**
 * Reji — the director console. Full-page, mobile-first, always-dark control
 * room (deliberate theme exception, like the guest Sahne): live preview +
 * server-timed progress ring, thumb-zone transport, the Flow grid with
 * goto / hide / spotlight, and the live audience sheet. State arrives over
 * the organizer WS (use-reji-ws); commands go through the audited REST route.
 */
export function RejiPage() {
  const { t } = useTranslation();
  const { slug, wedding } = useWedding();
  const api = useOrganizerApi();
  const queryClient = useQueryClient();
  const { slideState, presence, connected, clockOffsetRef } = useRejiWs(slug);

  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  }, []);

  // The Flow grid — WS drives refetches, no polling
  const playlistKey = ["organizer", slug, "reji-playlist"];
  const { data: playlist } = useQuery({
    queryKey: playlistKey,
    queryFn: async () => {
      const res = await api.get<RejiPlaylistResponse>("/slideshow/playlist");
      return res.data ?? { items: [], currentMediaId: null, total: 0 };
    },
  });

  // New approvals / hides change the loop length → refresh the grid
  const total = slideState?.total;
  useEffect(() => {
    if (total !== undefined) {
      void queryClient.invalidateQueries({ queryKey: playlistKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  // LIVE current slide for the grid highlight. The playlist snapshot only
  // refetches on loop-length changes, so its currentMediaId goes stale as the
  // show advances (the "video stuck as Sahnede" bug) — slide_state wins.
  // Held through title cards (media momentarily null).
  const [liveMediaId, setLiveMediaId] = useState<string | null>(null);
  const liveId = slideState?.media?.id;
  useEffect(() => {
    if (liveId) setLiveMediaId(liveId);
  }, [liveId]);

  const successKey: Partial<Record<ControlAction, string>> = {
    goto: "reji.gotoSuccess",
    hide: "reji.hideSuccess",
    unhide: "reji.unhideSuccess",
    spotlight: "reji.spotlightSuccess",
  };

  const sendControl = useCallback(
    async (action: ControlAction, mediaId?: string, toIndex?: number) => {
      setBusy(true);
      try {
        await api.post("/slideshow/control", { action, mediaId, toIndex });
        showToast(t(successKey[action] ?? "slideshowRemote.controlSuccess"));
        void queryClient.invalidateQueries({ queryKey: playlistKey });
      } catch (err) {
        showToast(err instanceof Error ? err.message : t("slideshowRemote.controlFailed"), false);
      } finally {
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, queryClient, showToast, t, slug],
  );

  const paused = slideState?.paused ?? false;
  const idle = slideState?.idle ?? false;
  const hasShow = (slideState?.total ?? 0) > 0;

  return (
    // Local always-dark control-room surface — token cascade goes dark for
    // this subtree only (deliberate exception, mirrors the guest Sahne).
    <div className="dark bg-background text-foreground fixed inset-0 z-50 flex flex-col overflow-hidden">
      {/* ── Header ── */}
      <header className="shrink-0 h-14 lg:h-16 flex items-center gap-3 px-4 lg:px-6 border-b border-white/10">
        <AppLink
          to={paths.wedding.dashboard(slug)}
          aria-label={t("nav.dashboard")}
          className="flex items-center justify-center size-9 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
        >
          <ArrowLeft className="h-5 w-5" />
        </AppLink>
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-white font-semibold leading-tight">
            <Clapperboard className="h-4.5 w-4.5 text-primary" />
            {t("reji.title")}
          </h1>
          <p className="text-white/40 text-[11px] uppercase tracking-[0.22em] leading-tight">
            {t("reji.subtitle")}
          </p>
        </div>
        <span
          className={cn(
            "ml-auto h-2 w-2 rounded-full shrink-0",
            connected ? "bg-emerald-400" : "bg-white/25",
          )}
          title={connected ? t("reji.live") : t("reji.connecting")}
        />
      </header>

      {/* ── Body: mobile single column; lg = control rail + Flow; xl adds a
             docked audience rail (a real control room, not a scaled phone) ── */}
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        <div className="shrink-0 flex flex-col lg:w-[420px] xl:w-[440px] lg:border-r lg:border-white/10 lg:overflow-y-auto">

      {/* ── Live status strip ── */}
      <div className="shrink-0 flex items-center gap-3 px-4 py-2.5 lg:px-6 lg:py-4">
        {paused ? (
          <span className="flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[11px] font-bold uppercase tracking-[0.18em] px-2.5 py-1">
            <Pause className="h-3 w-3" /> {t("slideshowRemote.paused")}
          </span>
        ) : idle || !hasShow ? (
          <span className="rounded-full bg-white/5 border border-white/15 text-white/55 text-[11px] font-semibold uppercase tracking-[0.14em] px-2.5 py-1">
            {hasShow ? t("reji.idleNoViewers") : t("slideshowRemote.offline")}
          </span>
        ) : (
          <span className="flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[11px] font-bold uppercase tracking-[0.18em] px-2.5 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {t("reji.live")}
          </span>
        )}

        {hasShow && slideState && (
          <span className="font-mono text-white/75 text-sm tabular-nums">
            {t("slideshowRemote.slideOf", { current: slideState.index + 1, total: slideState.total })}
          </span>
        )}

        {/* Audience facepile (<xl; the xl rail is always docked) — shared component */}
        <PresenceFacepile presence={presence} className="ml-auto xl:hidden" sheetZClassName="z-[70]" />
      </div>

      {/* ── Live preview ── */}
      <div className="shrink-0 px-4 lg:px-6">
        <RejiPreview
          slideState={slideState}
          clockOffsetRef={clockOffsetRef}
          weddingTitle={wedding?.title ?? t("reji.title")}
        />
      </div>

      {/* ── Transport ── */}
      <RejiTransport
        paused={paused}
        busy={busy}
        disabled={!hasShow}
        onPrev={() => sendControl("prev")}
        onToggle={() => sendControl(paused ? "resume" : "pause")}
        onNext={() => sendControl("next")}
      />

        </div>{/* end left rail */}

      {/* ── The Flow ── */}
      <div className="flex-1 min-h-0 overflow-y-auto safe-bottom">
        <RejiGrid
          items={playlist?.items ?? []}
          currentMediaId={liveMediaId ?? playlist?.currentMediaId ?? null}
          nextMediaId={slideState?.next?.id ?? null}
          busy={busy}
          onGoto={(id) => sendControl("goto", id)}
          onHide={(id) => sendControl("hide", id)}
          onUnhide={(id) => sendControl("unhide", id)}
          onSpotlight={(id) => sendControl("spotlight", id)}
          onMove={(id, toIndex) => sendControl("move", id, toIndex)}
        />
      </div>

      {/* ── xl: docked audience rail ── */}
      <aside className="hidden xl:flex xl:w-80 xl:shrink-0 xl:flex-col xl:border-l xl:border-white/10 xl:overflow-hidden">
        <RejiPresenceHeader presence={presence} />
        <AudienceList presence={presence} />
      </aside>

      </div>{/* end body */}


      {/* ── Toast ── */}
      {toast && (
        <div
          className={cn(
            "fixed left-1/2 -translate-x-1/2 bottom-6 z-[80] rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-lg pointer-events-none",
            toast.ok ? "bg-emerald-600" : "bg-red-600",
          )}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
